import type { ReactNode } from 'react';
import {
	Box,
	Stack,
	Typography,
	TypographyProps,
	useTheme,
} from '@mui/material';
import { Theme } from '@mui/material/styles';

import { CheckmarkCircleIcon, InfoIcon, InformationIcon } from '../../icons';

// TODO: redesign-dticket - fix the naming typo: Mlp -> Mpl
export interface IMlpIconMessageProps {
	type: EMlpIconTypes;
	variant: EMplVariantTypes;
	message: ReactNode;
	dataTestid?: string;
	textVariant?: TypographyProps['variant'];
}

interface IVariantStyles {
	iconColor?: string;
	textColor?: string;
	backgroundColor?: string;
	padding?: string;
}

export enum EMplVariantTypes {
	Default = 'DEFAULT',
	Info = 'INFO',
	Warning = 'WARNING',
	Error = 'ERROR',
	Success = 'SUCCESS',
}

// TODO: redesign-dticket - fix the naming typo: Mlp -> Mpl
export enum EMlpIconTypes {
	Info = 'INFO',
	Information = 'INFORMATION',
	Warning = 'WARNING',
	Success = 'SUCCESS',
}

const iconByType: Record<EMlpIconTypes, ReactNode> = {
	[EMlpIconTypes.Info]: <InfoIcon />,
	[EMlpIconTypes.Information]: <InformationIcon />,
	[EMlpIconTypes.Warning]: <InfoIcon />,
	[EMlpIconTypes.Success]: <CheckmarkCircleIcon />,
};

const getStylesByVariant = (
	variant: EMplVariantTypes,
	theme: Theme
): IVariantStyles => {
	const styleByVariantType = {
		[EMplVariantTypes.Default]: {
			iconColor: theme.palette.solutions?.[600],
			textColor: theme.palette.solutions?.[600],
		},
		[EMplVariantTypes.Info]: {
			iconColor: theme.palette.primary.main,
			textColor: theme.palette.primary.main,
			backgroundColor: theme.palette.solutions?.[50],
			padding: theme.spacing(1.5),
		},
		[EMplVariantTypes.Warning]: {
			iconColor: theme.palette.warning.main,
			textColor: theme.palette.primary.main,
			backgroundColor: theme.palette.warning.light,
			padding: theme.spacing(1.5),
		},
		[EMplVariantTypes.Error]: {
			iconColor: theme.palette.error.main,
			textColor: theme.palette.primary.main,
			backgroundColor: theme.palette.error?.light,
			padding: theme.spacing(1.5),
		},
		[EMplVariantTypes.Success]: {
			iconColor: theme.palette.success.dark,
			textColor: theme.palette.primary.main,
			backgroundColor: theme.palette.bewegt?.[50],
			padding: theme.spacing(1.5),
		},
	} as Record<EMplVariantTypes, IVariantStyles>;

	return (
		styleByVariantType[variant] ?? styleByVariantType[EMplVariantTypes.Default]
	);
};

export function MplIconMessage({
	type,
	variant,
	message,
	dataTestid,
	textVariant = 'body2',
}: IMlpIconMessageProps) {
	const theme = useTheme();
	const icon = iconByType[type];
	const variantStyle = getStylesByVariant(variant, theme);

	return (
		<Stack
			data-testid={dataTestid}
			direction="row"
			alignItems="center"
			gap={1}
			sx={{
				backgroundColor: variantStyle.backgroundColor,
				color: variantStyle.textColor,
				padding: variantStyle.padding,
				borderRadius: theme.spacing(2),
			}}
		>
			<Stack
				color={variantStyle.iconColor}
				alignItems="center"
				sx={{
					'& svg path': {
						fill: 'currentColor',
					},
				}}
			>
				{icon}
			</Stack>
			<Box flex={1}>
				<Typography
					variant={textVariant}
					sx={{
						whiteSpace: 'pre-line',
						wordBreak: 'break-word',
						overflowWrap: 'break-word',
						hyphens: 'auto',
					}}
				>
					{message}
				</Typography>
			</Box>
		</Stack>
	);
}
